import React from 'react';
import { Flame, Check } from 'lucide-react';

interface WeeklyProgressProps {
    completedDays: number; // 0-7
    currentDayIndex: number; // 0=Monday, 6=Sunday
    weekDays: string[]; // ["Po", "Út", ...]
    dailyMinutes: number;
    hasExamThisWeek?: boolean;
    examDayIndex?: number;
    dailyCompletionStatus?: boolean[];
    embedded?: boolean;
    currentStreak?: number;
    hasStreakToday?: boolean;
}

export const WeeklyProgress: React.FC<WeeklyProgressProps> = ({
    completedDays,
    currentDayIndex,
    weekDays,
    dailyMinutes,
    hasExamThisWeek = false,
    examDayIndex = -1,
    dailyCompletionStatus = [],
    embedded = false,
    currentStreak = 0,
    hasStreakToday = false
}) => {
    // Determine streak message based on status
    const getStreakMessage = () => {
        if (hasStreakToday) return "Série prodloužena! 🎉";
        if (currentStreak > 0) return "Pokračuj v sérii ještě dnes!";
        return "Začni svou sérii ještě dnes!";
    };


    const countText = `${currentStreak} ${currentStreak === 1 ? 'den' : (currentStreak >= 2 && currentStreak <= 4 ? 'dny' : 'dní')}`;

    return (
        <div className={`flex flex-col h-full font-poppins ${!embedded ? "bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-700" : ""}`}>

            {/* Top Section: Streak Info & Fire */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-extrabold text-[#FF9600] mb-1">
                        {countText} v řadě
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                        {getStreakMessage()}
                    </p>
                </div>

                {/* Flame Icon */}
                <div className="relative">
                    <Flame
                        className={`w-14 h-14 ${currentStreak > 0 || hasStreakToday ? "fill-[#FF9600] text-[#FF9600]" : "text-gray-300 dark:text-gray-600"}`}
                        strokeWidth={1.5}
                    />
                    {/* Optional: Add a little badge or animation if active */}
                </div>
            </div>

            {/* Bottom Section: Week Days */}
            <div className="grid grid-cols-7 gap-1 md:gap-3">
                {weekDays.map((day, idx) => {
                    const isToday = idx === currentDayIndex;
                    const isCompleted = dailyCompletionStatus[idx];
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const isFuture = idx > currentDayIndex;

                    // Determine Circle Style
                    let circleClasses = "bg-gray-200 dark:bg-gray-700 text-transparent"; // Default / Future
                    let content = null;

                    if (isCompleted) {
                        circleClasses = "bg-[#FF9600] text-white shadow-md shadow-orange-500/20";
                        content = <Check className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3} />;
                    } else if (isToday) {
                        // Current day, not completed yet
                        circleClasses = "bg-transparent border-2 border-gray-300 dark:border-gray-600 text-gray-400";
                        // Optional: Orange border if it's the target for today? 
                        // Let's stick to gray border to imply "empty slot" waiting to be filled, 
                        // maybe dashed or just solid.
                    }

                    return (
                        <div key={idx} className="flex flex-col items-center gap-3">
                            {/* Day Label */}
                            <span className={`text-xs md:text-sm font-bold uppercase tracking-wider ${isToday ? "text-[#FF9600]" : "text-gray-400 dark:text-gray-500"}`}>
                                {day}
                            </span>

                            {/* Circle Indicator */}
                            <div className={`
                                w-8 h-8 md:w-11 md:h-11 rounded-full flex items-center justify-center 
                                transition-all duration-300
                                ${circleClasses}
                            `}>
                                {content}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
